import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck } from "lucide-react";

import LiveLayout from "./LiveLayout";
import D13Footer from "../preview/D13Footer";
import StickyApplyBar from "./StickyApplyBar";
import HeroMedia from "./HeroMedia";
import { Star } from "./Star";
import { Icon } from "./Icon";
import { cld } from "../../lib/cloudinary";
import { HERO_VIDEO } from "../../lib/heroVideo";
import { CANDIDATE_JOURNEY, CANDIDATE_PROTECT, FEATURED_SECTORS, SECTOR_COUNT, FAQ_ITEMS } from "../../lib/adelContent";

import "../preview/Design13.css";
import "../preview/Design13Audience.css";
import "./Editorial.css";

/* ============================================================
   CandidatesHome — SHORT teaser landing for /candidates.
   Hero (See jobs / Submit CV) → live jobs strip → 4 teasers that
   link to dedicated pages → close. Warm, protective voice.
   FEE RULE: only ever "No registration fee".
   ============================================================ */

const sectorMini = FEATURED_SECTORS.slice(0, 4);

export default function CandidatesHome() {
  const heroRef = useRef(null);
  const { scrollYProgress: heroP } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroScale = useTransform(heroP, [0, 1], [1, 1.1]);
  const heroVeil = useTransform(heroP, [0, 1], [0.5, 0.82]);
  const heroTextY = useTransform(heroP, [0, 1], [0, -40]);

  return (
    <LiveLayout audience="candidate">
      {/* HERO — See jobs / Submit CV */}
      <section ref={heroRef} className="d13-hero d13-aud-hero d13-aud-hero--candidate">
        <HeroMedia video={HERO_VIDEO.candidate} className="d13-hero__img"
          alt="Kenyan job seekers welcomed in Nairobi, travelling, and working happily abroad" />
        <motion.div className="d13-hero__veil" style={{ opacity: heroVeil }} />
        <motion.div className="d13-hero__inner" style={{ y: heroTextY }}>
          <div className="d13-container">
            <motion.span className="d13-eyebrow d13-eyebrow--on-dark ed-eyebrow"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              <Star className="ed-eyebrow__star" /> For jobseekers
            </motion.span>
            <motion.h1 className="d13-hero__title"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
              Find a job abroad. <em>We line up the role.</em>
            </motion.h1>
            <motion.p className="d13-hero__sub"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45 }}>
              Real interviews, contracts you understand, and a team that stays with you — from your first call to long after you land.
            </motion.p>
            <motion.div className="d13-hero__ctas d13-hero__ctas--split"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }}>
              <Link to="/candidates/jobs" className="d13-btn d13-btn--primary">See open jobs <ArrowRight size={16} strokeWidth={2.2} /></Link>
              <Link to="/candidates/apply" className="d13-btn d13-btn--outline">Submit your CV <ArrowUpRight size={16} strokeWidth={2.2} /></Link>
            </motion.div>
            <motion.span className="hero-trust"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.85 }}>
              <ShieldCheck size={15} strokeWidth={2.2} /> No registration fee · Licensed recruitment agency
            </motion.span>
          </div>
        </motion.div>
      </section>

      {/* JOURNEY TEASER — register sand */}
      <section className="ed-band ed-band--sand ed-pad">
        <div className="ed-container">
          <header className="ed-opener">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> Your journey</span>
              <Link to="/candidates/journey" className="ed-more">See all 12 steps →</Link>
            </div>
            <h2 className="ed-h2">Twelve steps. Three phases. <em>Never on your own.</em></h2>
          </header>
          <div className="jt-cards">
            {CANDIDATE_JOURNEY.map((p, i) => (
              <Link key={p.id} to="/candidates/journey" className="jt-card">
                <img src={cld(p.image, { w: 800, q: "auto", f: "auto" })} alt={p.alt} loading="lazy" />
                <div className="jt-card__scrim" />
                <div className="jt-card__meta">
                  <span className="jt-card__kicker"><Star /> Phase {i + 1}</span>
                  <span className="jt-card__name">{p.name}</span>
                  <span className="jt-card__count">{p.steps.length} steps</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE PROTECT TEASER — register cream */}
      <section className="ed-band ed-band--cream ed-pad">
        <div className="ed-container">
          <header className="ed-opener">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> What we protect</span>
              <Link to="/candidates/journey" className="ed-more">How we protect you →</Link>
            </div>
            <h2 className="ed-h2">Four things we <em>guard for you.</em></h2>
          </header>
          <div className="d13-aud-protect__grid">
            {CANDIDATE_PROTECT.map((p, i) => (
              <motion.div key={p.t} className="d13-aud-protect__card" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, delay: i * 0.06 }}>
                <span className="d13-aud-protect__icon"><Icon name={p.icon} size={22} strokeWidth={1.8} /></span>
                <h3>{p.t}</h3>
                <p>{p.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS MINI — register sand */}
      <section id="sectors" className="ed-band ed-band--sand ed-pad">
        <div className="ed-container">
          <header className="ed-opener">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> Where you could work</span>
              <Link to="/candidates/sectors" className="ed-more">Explore all {SECTOR_COUNT} sectors →</Link>
            </div>
            <h2 className="ed-h2">Real roles, <em>across many trades.</em></h2>
          </header>
          <div className="sec-mini">
            {sectorMini.map((s) => (
              <Link key={s.slug} to="/candidates/sectors" className="sec-mini__tile">
                <img src={cld(`adel/v2/sector-${s.slug}`, { w: 600, q: "auto", f: "auto" })} loading="lazy" alt="" />
                <div className="sec-mini__tile-scrim" />
                <span>{s.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COMMON QUESTIONS — register cream (mirrors /faq styling; embeds answer-first Q&A for AEO) */}
      <section className="ed-band ed-band--cream ed-pad">
        <div className="ed-container">
          <header className="ed-opener">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> Common questions</span>
              <Link to="/faq" className="ed-more">See all questions →</Link>
            </div>
            <h2 className="ed-h2">Answers, <em>before you ask.</em></h2>
          </header>
          <div className="faq-list">
            {FAQ_ITEMS.filter((f) => f.audience === "candidate").slice(0, 4).map((f) => (
              <div key={f.q} className="faq-item">
                <h3 className="faq-q">{f.q}</h3>
                <p className="faq-a">{f.a}</p>
                {f.link && (
                  <p className="faq-a faq-verify">
                    <a href={f.link.href} target="_blank" rel="noreferrer">{f.link.label} →</a>
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSE — register navy */}
      <section className="ed-band ed-band--ink ed-pad">
        <div className="ed-container ed-cta">
          <motion.h2 className="ed-cta__t" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8 }}>
            Start today — no registration fee.
          </motion.h2>
          <p className="ed-cta__p">Send your details or apply on WhatsApp — a real person reads every application and replies.</p>
          <div className="ed-cta__row">
            <Link to="/candidates/apply" className="d13-btn d13-btn--primary">Submit your CV <ArrowRight size={16} strokeWidth={2.2} /></Link>
            <a href="tel:+254117222777" className="d13-btn d13-btn--ghost">Call +254 117 222 777</a>
          </div>
        </div>
      </section>

      <StickyApplyBar />
      <D13Footer />
    </LiveLayout>
  );
}
